import { Link } from 'react-router-dom';
import { Card, Button, Badge } from '@/components/ui';
import { ShoppingCart, ArrowRight, Package } from 'lucide-react';
import type { LowStockProduct } from './types';

interface LowStockWidgetProps {
  products: LowStockProduct[];
}

export function LowStockWidget({ products }: LowStockWidgetProps) {
  return (
    <Card padding="md" className="border-l-4 border-l-red-500">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-red-100 dark:bg-red-900/50 rounded-lg">
            <ShoppingCart className="text-red-600" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">
              À commander
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {products.length} produit{products.length > 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <Link to="/alerts">
          <Button variant="ghost" size="sm" className="text-xs">
            Voir tout
            <ArrowRight size={14} className="ml-1" />
          </Button>
        </Link>
      </div>

      {products.length === 0 ? (
        <div className="text-center py-6 text-gray-500 dark:text-gray-400">
          <Package size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">Tous les stocks sont OK</p>
        </div>
      ) : (
        <div className="space-y-2">
          {products.map(({ product, level, stockTotal }) => (
            <div
              key={product.id}
              className={`flex items-center justify-between p-2 sm:p-3 rounded-lg ${
                level === 'critical'
                  ? 'bg-red-50 dark:bg-red-900/30'
                  : 'bg-yellow-50 dark:bg-yellow-900/30'
              }`}
            >
              <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                <div
                  className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    level === 'critical' ? 'bg-red-500' : 'bg-yellow-500'
                  }`}
                />
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white text-sm truncate">
                    {product.produits?.nom || 'Produit inconnu'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Seuil:{' '}
                    {level === 'critical'
                      ? product.produits?.seuil_critique
                      : product.produits?.seuil_alerte}{' '}
                    {product.produits?.unite}
                  </p>
                </div>
              </div>
              <div className="text-right flex-shrink-0 ml-2">
                <p
                  className={`font-bold text-sm ${
                    level === 'critical' ? 'text-red-600' : 'text-yellow-600'
                  }`}
                >
                  {stockTotal.toFixed(1)} {product.produits?.unite}
                </p>
                <Badge variant={level === 'critical' ? 'danger' : 'warning'} size="sm">
                  {level === 'critical' ? 'Critique' : 'Alerte'}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
